import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Debug logging to verify base URL
if (import.meta.env.DEV) {
  console.log('API_BASE_URL:', API_BASE_URL);
  console.log('VITE_API_URL env:', import.meta.env.VITE_API_URL);
}

class APIClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private isRefreshing: boolean = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load tokens from localStorage
    this.accessToken = localStorage.getItem('accessToken');

    // Request interceptor - add auth token (always get fresh token from localStorage)
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Always get latest token from localStorage for multi-tab sync
        const currentToken = localStorage.getItem('accessToken');
        if (currentToken && config.headers) {
          config.headers.Authorization = `Bearer ${currentToken}`;
          this.accessToken = currentToken; // Update internal cache
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle errors and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };

        // If 401 and we have a refresh token, try to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          const refreshToken = localStorage.getItem('refreshToken');

          if (!refreshToken) {
            // No refresh token, redirect to login
            this.clearTokens();
            toast.error('Session expired. Please login again.');
            window.location.href = '/auth/login';
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          // If already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(this.client(originalRequest));
              });
            });
          }

          this.isRefreshing = true;

          try {
            const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
              refreshToken,
            });

            const { accessToken } = response.data.data || response.data;

            // Update tokens
            this.setTokens(accessToken, refreshToken);

            // Notify all queued requests
            this.refreshSubscribers.forEach((callback) => callback(accessToken));
            this.refreshSubscribers = [];

            // Retry original request
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }

            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            this.clearTokens();
            this.refreshSubscribers = [];

            toast.error('Session expired. Please login again.');
            window.location.href = '/auth/login';
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Handle other errors
        if (error.response?.status === 403) {
          toast.error('Access denied. You do not have permission to perform this action.');
        } else if ((error.response?.status ?? 0) >= 500) {
          toast.error('Server error. Please try again later.');
        } else if (error.response?.status === 400) {
          const message = (error.response?.data as any)?.message || 'Invalid request';
          toast.error(message);
        } else if (error.message === 'Network Error') {
          toast.error('Network error. Please check your connection.');
        } else {
          const message = (error.response?.data as any)?.message || error.message || 'An error occurred';
          // Only show toast if not a 401 (already handled above)
          if (error.response?.status !== 401) {
            toast.error(message);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  setTokens(accessToken: string, refreshToken: string) {
    this.accessToken = accessToken;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  }

  clearTokens() {
    this.accessToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('openai_token'); // Also clear OpenAI token
  }

  getAccessToken() {
    return this.accessToken;
  }

  // Generic request methods
  async get<T>(url: string, config?: any): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data as T;
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data as T;
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data as T;
  }

  async delete<T>(url: string, config?: any): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data as T;
  }
}

export const apiClient = new APIClient();
